import { Autocomplete, ListSubheader, TextField } from '@mui/material'
import React, { ReactElement, ReactNode } from 'react'

type SearchableDropdownProps<ValueType> = {
  label: string
  items: { value: ValueType; label: ReactNode; subHeader?: string }[]
  value: ValueType
  fullWidth?: boolean
  disabled?: boolean
  margin?: 'none' | 'dense' | 'normal'
  style?: React.CSSProperties
  error?: boolean
  handleChange: (value: ValueType | null) => void
  'data-testid'?: string
}

export default function SearchableDropdown<ValueType extends string | number | undefined>({
  label,
  items,
  value,
  fullWidth,
  disabled = false,
  margin = 'none',
  style,
  error,
  handleChange,
  'data-testid': dataTestId,
}: SearchableDropdownProps<ValueType>): ReactElement {
  const language = localStorage.getItem('language') || 'sv'

  // Group items by subHeader
  const groupedItems = items.reduce<Record<string, { value: ValueType; label: ReactNode }[]>>((groupedItems, item) => {
    const groupKey = item.subHeader || 'default'
    if (!groupedItems[groupKey]) {
      groupedItems[groupKey] = []
    }
    groupedItems[groupKey].push(item)
    return groupedItems
  }, {})

  const sortedGroupObjects = Object.entries(groupedItems).sort(([a], [b]) => a.localeCompare(b, language))

  function sortGroupedItems<ValueType>(
    groupItems: { value: ValueType; label: ReactNode }[]
  ): { value: ValueType; label: ReactNode }[] {
    return groupItems.sort((itemA, itemB) => String(itemA.label).localeCompare(String(itemB.label), language))
  }

  const dropdownItems = sortedGroupObjects.flatMap(([subHeader, groupItems]) => {
    const subHeaderItem = subHeader !== 'default' ? { value: '', label: subHeader, isSubHeader: true } : null
    const itemsWithSubHeader = sortGroupedItems(groupItems).map((item) => ({ ...item, isSubHeader: false }))
    return subHeaderItem ? [subHeaderItem, ...itemsWithSubHeader] : itemsWithSubHeader
  })

  return (
    <Autocomplete
      disablePortal
      fullWidth={fullWidth}
      options={dropdownItems}
      getOptionLabel={(option) => (option.isSubHeader ? '' : (option.label as string))}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
      value={dropdownItems.find((item) => item.value === value) || null}
      onChange={(_event, newValue) => {
        handleChange((newValue?.value as ValueType) ?? null)
      }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin={margin}
          error={error}
          style={style}
          // Merge into the Autocomplete-provided inputProps so role/aria/ref bits are preserved.
          inputProps={{ ...params.inputProps, 'data-testid': dataTestId }}
        />
      )}
      renderOption={(props, option) =>
        option.isSubHeader ? (
          <ListSubheader component="li" color="inherit" key={option.label as string}>
            {option.label}
          </ListSubheader>
        ) : (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )
      }
    />
  )
}
